<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  TagsInputRoot,
} from 'radix-vue'
import { Input } from '@/components/ui/input'
import { Check, ChevronDown } from 'lucide-vue-next'
import { getAgencesID } from '@/lib/localStorage'
import type { GetUserAgencesViewModel } from '@/lib/types'
import Label from '@/components/ui/label/Label.vue'
import BaseRepository from '@/repository/BaseRepository'

const emit = defineEmits(['change'])
const options = ref<GetUserAgencesViewModel[]>([])

const fetchOptions = async () => {
  try {
    const result = await BaseRepository.getAgences()
    options.value = Array.isArray(result) ? result : [result]
  } catch (error) {
    console.error('Error fetching options:', error)
  }
}

const allOptions = computed(() => {
  return [{ id: 'all', title: 'Toutes' }, ...(options.value ?? [])]
})

const agences = ref<(string | number)[]>([])
const searchTerm = ref('')
const filteredOptions = computed(() => {
  if (!searchTerm.value) return allOptions.value
  return allOptions.value.filter((option) =>
    option.title.toLowerCase().includes(searchTerm.value.toLowerCase()),
  )
})

const handleSelectionChange = (newValue: string | number | (string | number)[]) => {
  const newValues = Array.isArray(newValue) ? newValue : [newValue]

  if (newValues.includes('all') && options.value) {
    if (agences.value.length === options.value.length) {
      agences.value = []
    } else {
      agences.value = options.value.map((option) => option.id)
    }
  } else {
    agences.value = newValues
  }

  localStorage.setItem('agences_ids', JSON.stringify(agences.value))
  searchTerm.value = ''
  emit('change')
}

onMounted(async () => {
  await fetchOptions()
  agences.value = await getAgencesID()
})
</script>

<template>
  <div class="flex flex-col gap-2">
    <Label>Agences</Label>
    <ComboboxRoot
      :modelValue="agences"
      @update:modelValue="handleSelectionChange"
      v-model:search-term="searchTerm"
      multiple
      class="mx-auto relative"
    >
      <ComboboxTrigger>
        <ComboboxAnchor
          class="inline-flex justify-between items-center gap-2 whitespace-nowrap rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 w-[240px] ps-3 text-start font-normal"
        >
          <TagsInputRoot
            v-model="agences as string[]"
            delimiter=""
            class="flex gap-2 rounded-lg flex-wrap"
          >
            <span v-if="agences.length == options?.length">Toutes les agences</span>
            <span v-else>{{ agences.length }} agences sélectionnées</span>
          </TagsInputRoot>
          <ChevronDown />
        </ComboboxAnchor>
      </ComboboxTrigger>

      <ComboboxContent
        class="absolute h-48 z-10 w-full mt-2 bg-white overflow-hidden rounded shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
      >
        <ComboboxViewport class="p-[5px]">
          <Input
            v-model="searchTerm"
            placeholder="Rechercher une agence..."
            class="focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0"
            @keydown.enter.prevent
          />
          <ComboboxEmpty class="text-gray-400 text-xs font-medium text-center py-2">
            Aucune agence trouvée
          </ComboboxEmpty>
          <ComboboxGroup>
            <ComboboxItem
              v-for="option in filteredOptions"
              :key="option.id"
              class="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:cursor-pointer"
              :value="option.id"
            >
              <ComboboxItemIndicator
                class="absolute right-2 flex h-3.5 w-3.5 items-center justify-center"
              >
                <Check class="h-4 w-4" />
              </ComboboxItemIndicator>
              <span>
                {{ option.title }}
              </span>
            </ComboboxItem>
          </ComboboxGroup>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>
  </div>
</template>
